/**
 * Stoff und Stahl — Personalisierung auf der Produktseite.
 *
 * Ablauf beim Klick auf „In den Warenkorb“:
 *   1. Eigene Felder prüfen (Modell, drei Fotos mit Mindestmaß, Bildrechte).
 *   2. Modell, Infotext und Bildrechte als Warenkorb-Attribute speichern
 *      (POST /cart/update.js, JSON — Dateien lehnt Shopify dort mit 422 ab).
 *   3. Dann das Horizon-Produktformular abschicken: Die Foto-Felder gehören per
 *      form="…" zum Formular und gehen als Line-Item-Properties mit hoch;
 *      Shopify speichert die URL der Datei als Property-Wert.
 *
 * Liegt die Personalisierung schon im Warenkorb (Artikel mit Foto-Properties und
 * Attribut „Modell“), zeigt die Seite eine Zusammenfassung; der neue Artikel geht
 * ohne Dateien, nur mit der Markierung properties[_personalisierung], in den Warenkorb.
 */

const ATTRIBUTE = { modell: 'Modell', infotext: 'Infotext', rechte: 'Bildrechte' };
const FOTO_PROPERTIES = { seite: 'Foto Seite', front: 'Foto Front', heck: 'Foto Heck' };

// Fotos werden im Browser zu JPEG gewandelt und auf höchstens MAX_KANTE Pixel (lange Kante)
// verkleinert: kleinerer Upload, einheitliches Format. Das Mindestmaß wird vorher am Original
// geprüft, also an dem, was der Kunde wirklich hochgeladen hat.
//
// 3500 px statt der früheren 5000 (Messung vom 05.10.2026). Das Layout setzt die Seitenansicht
// mit 2197 px Breite in die Druckdatei; bei 3500 px muss das Moped 63 % der langen Kante
// einnehmen, damit nichts hochskaliert wird — bei 5000 px waren es 44 %, aber der Upload war
// mit 6,6 MB für drei Fotos doppelt so groß und dauerte auf einer Mobilverbindung rund eine
// halbe Minute. Die Konturzeichnung bleibt unverändert, weil die Strichstärke bei 3500 px noch
// vom Prozentwert bestimmt wird (0,12 % = 4,2 px) und nicht von der 3-px-Untergrenze.
const MAX_KANTE = 3500;
const JPEG_QUALITAET = 0.92;

// Oberhalb von etwa 20 MB je Anfrage antwortet Shopifys nginx mit 413 und der Artikel landet
// NICHT im Warenkorb — ohne Fehlermeldung, der Knopf sieht unverändert aus. Genau das haben
// Kunden als „ich kann nichts in den Warenkorb legen" gemeldet (gemessen am 05.10.2026:
// 15,3 MB gehen durch, 23,3 MB ergeben 413). Deshalb ein eigener Deckel mit Sicherheitsabstand:
// Reißt die Summe der drei Fotos das Budget, werden sie stufenweise kleiner gerechnet, und der
// Kaufknopf wird erst danach freigegeben.
const UPLOAD_BUDGET = 15 * 1024 * 1024;
const NOTFALL_STUFEN = [
  { kante: 3000, qualitaet: 0.9 },
  { kante: 2500, qualitaet: 0.88 },
  { kante: 2000, qualitaet: 0.85 },
];

// Wie lange nach dem Absenden auf den Artikel im Warenkorb gewartet wird, bevor der Ladezustand
// als Fehlschlag gilt. Großzügig: 15 MB brauchen auf einer schwachen Mobilverbindung über eine
// Minute, und ein zu früher Abbruch wäre schlimmer als ein paar Sekunden zu viel Geduld.
const UPLOAD_GEDULD_MS = 90000;

const MODELL_NAMEN = { S51: 'Simson S51', S50: 'Simson S50', STAR: 'Simson Star', SCHWALBE: 'Simson Schwalbe', SR50: 'Simson SR50' };

class StoffUndStahlPersonalisierung extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('.sus');
    if (!this.root) return;

    // Mindestmaß auf der LANGEN KANTE, für alle drei Ansichten (Felix, 28.09.2026).
    // Der Name minWidth/min_width bleibt, damit die Einstellung im Theme gültig bleibt.
    this.minKante = Number(this.root.dataset.minWidth) || 500;
    this.maxBytes = (Number(this.root.dataset.maxMb) || 20) * 1024 * 1024;
    this.maxChars = Number(this.root.dataset.maxChars) || 700;
    this.texte = this.#leseTexte();

    this.form = document.getElementById(this.root.dataset.formId);
    this.modellSelect = this.root.querySelector('[data-sus-modell]');
    this.textarea = this.root.querySelector('[data-sus-infotext]');
    this.zaehler = this.root.querySelector('[data-sus-zaehler]');
    this.rechte = this.root.querySelector('[data-sus-rechte]');
    this.fehler = this.root.querySelector('[data-sus-fehler]');
    this.hinweis = this.root.querySelector('[data-sus-hinweis]');
    this.formular = this.root.querySelector('[data-sus-formular]');
    this.vorhanden = this.root.querySelector('[data-sus-vorhanden]');
    this.vorhandenText = this.root.querySelector('[data-sus-vorhanden-text]');
    this.dateiInputs = Array.from(this.root.querySelectorAll('[data-sus-datei]'));
    this.zeitstempel = this.root.querySelector('[data-sus-zeitstempel]');
    this.fotoUrlFelder = Array.from(this.root.querySelectorAll('[data-sus-fotourl]'));
    this.minKanteFeld = this.root.querySelector('[data-sus-minkante]');
    this.fehltBox = this.root.querySelector('[data-sus-fehlt]');
    this.fehltListe = this.root.querySelector('[data-sus-fehlt-liste]');
    this.weiterFrei = this.root.querySelector('[data-sus-weiter-frei]');
    this.weiterDrin = this.root.querySelector('[data-sus-weiter-drin]');
    this.textSchonDrin = this.root.dataset.schonDrin || 'Dieses Teil liegt schon im Warenkorb.';
    this.textLaedt = this.root.dataset.laedt || 'Deine Fotos werden hochgeladen';
    this.panneBasis = this.root.dataset.panneBasis || '';
    this.textVariante = this.root.dataset.variante || 'Die Auswahl wird übernommen …';
    this.textLaedtFehler =
      this.root.dataset.laedtFehler ||
      'Das hat nicht geklappt. Entweder war die Verbindung zu langsam oder die Fotos zu groß. ' +
      'Versuch es bitte noch einmal.';

    /** @type {Record<string, {file: File, quelle: File, width: number, height: number}>} */
    this.geprueft = {};
    this.textUnveraendert = true;
    this.vorhandenImWarenkorb = false;
    this.aendernAktiv = false;
    this.sperreAktiv = false;
    // Solange Horizon einen Variantenwechsel bearbeitet, darf nicht gekauft werden.
    // Begruendung in #variantenwechselUeberwachen().
    this.variantenwechsel = false;
    /** Varianten-IDs, die schon im Warenkorb liegen. */
    this.korbVarianten = new Set();

    this.modellSelect.addEventListener('change', () => {
      this.#modellGewechselt();
      this.#zustandAktualisieren();
    });
    this.textarea.addEventListener('input', () => {
      this.textUnveraendert = false;
      this.#zaehlerAktualisieren();
      this.#zustandAktualisieren();
    });
    this.rechte.addEventListener('change', () => this.#zustandAktualisieren());
    this.root.querySelector('[data-sus-standardtext]').addEventListener('click', () => {
      this.#standardtextEinsetzen(true);
    });
    this.root.querySelector('[data-sus-aendern]').addEventListener('click', () => {
      this.aendernAktiv = true;
      this.vorhanden.hidden = true;
      this.formular.hidden = false;
      this.#zustandAktualisieren();
    });
    for (const input of this.dateiInputs) {
      // #dateiPruefen hat mehrere Ausstiege; der Zustand wird darum hier
      // nach jedem Durchlauf neu berechnet, egal wie er endete.
      input.addEventListener('change', async () => {
        await this.#dateiPruefen(input);
        this.#zustandAktualisieren();
      });
    }

    this.#zaehlerAktualisieren();
    this.#kaufknoepfeBeobachten();
    this.#zustandAktualisieren();

    if (!this.form) {
      console.warn('[stoffundstahl] Produktformular nicht gefunden:', this.root.dataset.formId);
    }

    this.#frischeUeberwachen();

    this.#warenkorbLesen();
  }

  disconnectedCallback() {
    if (this.beobachter) this.beobachter.disconnect();
    for (const [ziel, name, fn] of this.abmelden || []) ziel.removeEventListener(name, fn);
  }

  /**
   * Der Kaufknopf bleibt gesperrt, solange Horizon einen Variantenwechsel
   * bearbeitet. Das ist keine Vorsicht, sondern deckt einen Fehler ab, der eine
   * Bestellung OHNE Fotos erzeugt (gemessen am 20.10.2026):
   *
   *   // assets/product-form.js
   *   handleSubmit(event) {
   *     event.preventDefault();
   *     if (this.#variantChangeInProgress) {
   *       this.#addToCartQueue.push(this.#createQueuedAddToCartItem());
   *       return;                     // <- die Formulardaten sind hier verloren
   *     }
   *     this.#processAddToCart(undefined, undefined, event);
   *   }
   *
   * Der eingereihte Klick geht danach ueber #processBatchAddToCart als JSON mit
   * nur { id, quantity } raus. Keine Formulardaten heisst: keine Fotos, kein
   * Modell, kein Infotext. Am Live-Shop belegt: 79 Bytes application/json statt
   * multipart, und die Position lag ohne jede Eigenschaft im Warenkorb. Auf
   * schneller Leitung passiert es kaum, auf einer Mobilverbindung leicht: wer
   * die Groesse waehlt und gleich danach kauft, trifft das Fenster.
   *
   * Woran der Wechsel zu erkennen ist: Horizon hoert auf 'shopify:product:select'
   * und haelt sich dessen `promise` als #pendingVariantChange. Dasselbe Ereignis
   * und dieselbe Zusage nehmen wir, damit die Sperre genau so lange gilt wie der
   * Wechsel. Eine Notbremse nach 20 Sekunden ist dabei, damit ein haengendes
   * Versprechen den Knopf nicht fuer immer sperrt.
   */
  #variantenwechselUeberwachen(anmelden) {
    const beiWahl = (ereignis) => {
      this.variantenwechsel = true;
      this.#zustandAktualisieren();
      const fertig = () => {
        this.variantenwechsel = false;
        this.#zustandAktualisieren();
      };
      const zusage = ereignis && ereignis.promise;
      let notbremse = setTimeout(fertig, 20000);
      const loesen = () => { clearTimeout(notbremse); fertig(); };
      if (zusage && typeof zusage.then === 'function') zusage.then(loesen, loesen);
      else setTimeout(loesen, 1200);
    };
    // Auf document und in der Einfangphase: das Ereignis wird am Abschnitt
    // abgefangen, und wir wollen es unabhaengig davon sehen, wo der Block haengt.
    anmelden(document, 'shopify:product:select', beiWahl, true);
  }

  // ---------- Aktueller Warenkorbstand ----------

  /**
   * Der Warenkorb kann sich ausserhalb dieser Seite aendern - in der
   * Warenkorb-Schublade, in einem anderen Tab, ueber den Zurueck-Knopf.
   * Ohne Auffrischen bliebe der Kaufknopf mit „liegt schon im Warenkorb“
   * gesperrt, obwohl er leer ist. Darum an diesen Stellen nachlesen.
   */
  #frischeUeberwachen() {
    this.abmelden = [];
    const auffrischen = () => this.#warenkorbAuffrischen();
    // Beim Wechsel der Sichtbarkeit nur nachlesen, wenn die Seite wieder
    // sichtbar wird - beim Weggehen waere es verschwendet. Ein Zeigerdruck
    // dagegen heisst immer, dass jemand davor sitzt.
    const beiSichtbarkeit = () => {
      if (document.visibilityState === 'visible') this.#warenkorbAuffrischen();
    };
    const anmelden = (ziel, name, fn, optionen) => {
      ziel.addEventListener(name, fn, optionen);
      this.abmelden.push([ziel, name, fn]);
    };

    this.#variantenwechselUeberwachen(anmelden);

    anmelden(document, 'visibilitychange', beiSichtbarkeit);
    anmelden(window, 'pageshow', auffrischen);
    anmelden(document, 'shopify:cart:lines-update', () => this.#warenkorbLesen());

    // Ein gesperrter Knopf feuert selbst keine Zeigerereignisse, der Bereich
    // darum herum schon. So wird der Stand genau dann geprueft, wenn jemand
    // den gesperrten Knopf zu druecken versucht.
    const bereich = this.form ? this.form.closest('product-form-component') : null;
    if (bereich) anmelden(bereich, 'pointerdown', auffrischen, true);
  }

  // ---------- Sperre des Kaufknopfs ----------

  /** Die gerade gewählte Variante, wie sie im Produktformular steht. */
  #aktuelleVariante() {
    const feld = this.form ? this.form.querySelector('[name="id"]') : null;
    return feld ? String(feld.value) : '';
  }

  /** Liegt genau diese Variante schon im Warenkorb? */
  #schonImWarenkorb() {
    const id = this.#aktuelleVariante();
    return id !== '' && this.korbVarianten.has(id);
  }

  /**
   * Zeigt im Kasten „schon hinterlegt“ den passenden nächsten Schritt:
   * entweder die Beruhigung, dass die Fotos übernommen werden, oder den
   * Weg zum anderen Teil, wenn dieses hier schon im Warenkorb liegt.
   */
  #weiterSchalten() {
    const drin = this.#schonImWarenkorb();
    if (this.weiterFrei) this.weiterFrei.hidden = drin || !this.vorhandenImWarenkorb;
    if (this.weiterDrin) this.weiterDrin.hidden = !drin;
  }

  /**
   * Was der Kunde noch erledigen muss, in der Reihenfolge der Felder.
   * Leere Liste heisst: alles da, der Knopf darf aktiv sein.
   */
  #fehlendes() {
    // Liegt die Personalisierung schon im Warenkorb und wird nicht geaendert,
    // ist der Artikel ein Folgeartikel derselben Bestellung - dann fehlt nichts.
    if (this.vorhandenImWarenkorb && !this.aendernAktiv) return [];

    const fehlt = [];
    if (!this.modellSelect.value) fehlt.push('Dein Modell auswählen');
    for (const input of this.dateiInputs) {
      if (!this.geprueft[input.dataset.susDatei]) fehlt.push(input.dataset.susBeschriftung);
    }
    if (!this.rechte.checked) fehlt.push('Bestätigen, dass du die Rechte an den Fotos hast');
    if (this.textarea.value.length > this.maxChars) {
      fehlt.push(`Text auf höchstens ${this.maxChars} Zeichen kürzen`);
    }
    return fehlt;
  }

  /**
   * Alle Schaltflächen, die einen Artikel in den Warenkorb legen: die des
   * Produktformulars und die der eingeblendeten Leiste am unteren Rand.
   */
  #kaufknoepfe() {
    const knoepfe = new Set();
    if (this.form) {
      for (const b of this.form.querySelectorAll('button[type="submit"], input[type="submit"]')) knoepfe.add(b);
    }
    for (const b of document.querySelectorAll('add-to-cart-component button[name="add"]')) knoepfe.add(b);
    return Array.from(knoepfe);
  }

  /**
   * Schreibt nur, wenn sich wirklich etwas aendert. Sonst loest jeder
   * Schreibvorgang den Beobachter aus, der wieder schreibt - eine
   * Endlosschleife, die die Seite einfriert.
   */
  #knoepfeSetzen() {
    const sekunden = this.laedt ? Math.max(0, Math.round((Date.now() - this.laedtSeit) / 1000)) : 0;
    const ladeText = `${this.textLaedt} … ${sekunden} s`;
    for (const b of this.#kaufknoepfe()) {
      // Ladezustand: Beschriftung merken, umschreiben, spaeter zurueckgeben. Auch hier gilt
      // die Regel von oben - nur schreiben, wenn sich wirklich etwas aendert.
      const knoten = StoffUndStahlPersonalisierung.#knopfBeschriftung(b);
      if (knoten) {
        if (this.laedt) {
          if (b.dataset.susLabel === undefined) b.dataset.susLabel = knoten.textContent;
          if (knoten.textContent !== ladeText) knoten.textContent = ladeText;
          if (b.dataset.susLaedt !== '1') b.dataset.susLaedt = '1';
        } else if (b.dataset.susLabel !== undefined) {
          if (knoten.textContent !== b.dataset.susLabel) knoten.textContent = b.dataset.susLabel;
          delete b.dataset.susLabel;
          delete b.dataset.susLaedt;
        }
      }

      if (this.sperreAktiv) {
        if (b.dataset.susGesperrt !== '1') b.dataset.susGesperrt = '1';
        if (!b.disabled) b.disabled = true;
        if (b.getAttribute('aria-disabled') !== 'true') b.setAttribute('aria-disabled', 'true');
      } else if (b.dataset.susGesperrt === '1') {
        // Nur zurueckgeben, was wir selbst gesperrt haben - ein Knopf, den
        // Shopify wegen Ausverkauf deaktiviert hat, bleibt deaktiviert.
        delete b.dataset.susGesperrt;
        if (b.disabled) b.disabled = false;
        b.removeAttribute('aria-disabled');
      }
    }
  }

  /**
   * Horizon baut die Kaufschaltflaechen bei jedem Variantenwechsel neu auf und
   * aktiviert sie dabei. Der Beobachter setzt die Sperre danach wieder.
   */
  #kaufknoepfeBeobachten() {
    const bereiche = [
      this.form ? this.form.closest('product-form-component') : null,
      document.querySelector('sticky-add-to-cart'),
    ].filter(Boolean);
    if (!bereiche.length) return;

    // Jede Veraenderung im Kaufbereich kann ein Variantenwechsel sein -
    // dann gilt eine andere Varianten-ID und die Sperre wird neu bewertet.
    this.beobachter = new MutationObserver(() => this.#zustandAktualisieren());
    for (const bereich of bereiche) {
      this.beobachter.observe(bereich, {
        childList: true,
        subtree: true,
        attributes: true,
        // 'value' ist das versteckte Feld mit der Varianten-ID; Horizon
        // schreibt es beim Wechsel der Groesse oder Farbe um.
        attributeFilter: ['disabled', 'value'],
      });
    }

    // Die Variantenauswahl meldet sich als change-Ereignis am Formular.
    // Setzt Horizon die ID als Eigenschaft statt als Attribut, greift der
    // Beobachter nicht - dieser Weg schon.
    if (this.form) {
      this.form.addEventListener('change', () => this.#zustandAktualisieren());

      // Ladezustand: NICHT abfangen, nur zusehen. Horizon haengt seinen eigenen
      // Handler mit capture: true an document und sendet vor uns ab - genau daraus
      // entstand am 27.09.2026 der Doppelversand. Hier wird in der Blasenphase nur
      // mitgehoert und der Knopf beschriftet; am Absenden selbst aendert sich nichts.
      this.form.addEventListener('submit', () => this.#ladenStarten());
    }
  }

  #zustandAktualisieren() {
    // Die Wiedereintrittssperre muss auch dann fallen, wenn unterwegs etwas
    // wirft - sonst bliebe der Zustand fuer immer eingefroren.
    if (this.rechnetGerade) return;
    this.rechnetGerade = true;
    try {

      const fehlt = this.#fehlendes();
      const drin = this.#schonImWarenkorb();

      // Folgeartikel derselben Bestellung brauchen nichts zu sichern - die
      // Attribute liegen schon im Warenkorb.
      const folgeartikel = this.vorhandenImWarenkorb && !this.aendernAktiv;

      // Welche Felder mit dem Formular mitgehen, wird NUR hier entschieden und
      // bei jedem Durchlauf neu aus dem Warenkorbstand abgeleitet. Vorher stand
      // es an drei Stellen und wurde nie zurückgenommen: Verschwand die Position
      // mit den Fotos aus dem Warenkorb, blieben die Datei-Felder abgeschaltet
      // und die Markierung an — die Bestellung kam ohne Fotos an, nach der
      // Bezahlung (Auftrag 100434, 29.09.2026).
      this.#fotoFelderSchalten(!folgeartikel);

      const fp = this.#fingerabdruck();
      const sichernOffen =
      !folgeartikel &&
      this.#angabenVollstaendig() &&
      fp !== this.gesichert &&
      fp !== this.gescheitertAn;

      // Gesperrt wird aus drei Gründen: die Personalisierung ist unvollständig,
      // genau diese Variante liegt schon im Warenkorb, oder die Angaben sind
      // noch nicht im Warenkorb gesichert. Ohne den zweiten Grund liesse sich
      // dasselbe Teil beliebig oft hinzufügen, ohne den dritten könnte ein
      // Artikel ohne Modell und Infotext hineinrutschen.
      // Waehrend des Uploads ebenfalls gesperrt - ein zweiter Klick wuerde denselben
      // Artikel noch einmal hochladen, und zwar mit denselben Fotos.
      this.sperreAktiv = fehlt.length > 0 || drin || sichernOffen
        || this.laedt === true || this.variantenwechsel === true;
      if (sichernOffen) this.#sichernAnstossen();

      if (this.fehltBox && this.fehltListe) {
      const zeigen = fehlt.length > 0 || drin || sichernOffen || this.variantenwechsel === true;
      this.fehltBox.hidden = !zeigen;
      let eintraege = [];
      if (fehlt.length > 0) eintraege = fehlt;
      else if (drin) eintraege = [this.textSchonDrin];
      else if (sichernOffen) eintraege = ['Angaben werden gespeichert …'];
      else if (this.variantenwechsel === true) eintraege = [this.textVariante];
      this.fehltListe.replaceChildren(
        ...eintraege.map((text) => {
          const li = document.createElement('li');
          li.textContent = text;
          return li;
        })
      );
      const titel = this.fehltBox.querySelector('.sus__fehlt-titel');
      if (titel) titel.hidden = fehlt.length === 0;
      this.fehltBox.classList.toggle('sus__fehlt--warten',
        fehlt.length === 0 && !drin && (sichernOffen || this.variantenwechsel === true));
      this.fehltBox.classList.toggle('sus__fehlt--drin', fehlt.length === 0 && drin);
      }

      this.#weiterSchalten();
      this.#knoepfeSetzen();
    } finally {
      this.rechnetGerade = false;
    }
  }

  /**
   * Nur hinsehen, nichts heilen (20.10.2026): meldet einen Fehler beim
   * Verkleinern oder Hochladen an den Server. Geraeteerkennung und Versand
   * stehen in assets/stoffundstahl-panne.js; fehlt die Datei, passiert nichts.
   * Es gehen keine Fotos und keine Mailadresse mit.
   */
  #panne(schritt, fehler, zusatz) {
    if (window.susPanne) {
      window.susPanne(this.panneBasis, schritt, fehler,
        Object.assign({ quelle: 'warenkorb' }, zusatz || {}));
    }
  }

  /**
   * Ein Ereignis des Kaufwegs melden. Als Abfrageparameter ohne Rumpf, sonst
   * loest der Beacon eine CORS-Vorabanfrage aus, die er nicht fuehren kann
   * (19.10.2026). Darf nie etwas kaputtmachen, deshalb alles in try.
   */
  #melden(ereignis) {
    try {
      if (!this.panneBasis) return;
      // Automatische Laeufe zaehlen nicht mit. Jeder Aufruf des Testkaeufers
      // traegt utm_source=test (harte Regel, Teil 5) - sonst haette jeder
      // Roboterlauf einen Warenkorb in den Zahlen erzeugt, 16 am Tag, und die
      // Fruehwarnung "Warenkoerbe, aber niemand an der Kasse" waere allein
      // davon losgegangen.
      if (/[?&]utm_source=test(&|$)/.test(location.search)) return;
      const ziel = `${this.panneBasis}/webhook/vorschau/ereignis`
        + `?ereignis=${encodeURIComponent(ereignis)}`;
      if (navigator.sendBeacon) navigator.sendBeacon(ziel);
      else fetch(ziel, { method: 'POST', keepalive: true }).catch(() => {});
    } catch (e) { /* eine Messung haelt den Kauf nie auf */ }
  }

  #leseTexte() {
    try {
      return JSON.parse(this.root.querySelector('[data-sus-texte]').textContent);
    } catch (e) {
      console.warn('[stoffundstahl] Standardtexte nicht lesbar', e);
      return {};
    }
  }

  // ---------- Warenkorb-Zustand ----------

  /** Artikel im Warenkorb, der alle drei Foto-Properties trägt (bei mehreren der jüngste). */
  static #fotoArtikel(cart) {
    let bester = null;
    for (const item of cart.items || []) {
      const p = item.properties || {};
      const komplett = Object.values(FOTO_PROPERTIES).every((k) => typeof p[k] === 'string' && p[k].startsWith('http'));
      if (!komplett) continue;
      const zeit = String(p['_personalisiert_am'] || '');
      if (!bester || zeit > bester.zeit) bester = { item, zeit };
    }
    return bester ? bester.item : null;
  }

  async #korbHolen() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
  }

  /** Nur die Daten übernehmen, ohne an sichtbaren Feldern zu rühren. */
  #standUebernehmen(cart) {
    this.korbVarianten = new Set((cart.items || []).map((i) => String(i.variant_id)));
    const a = cart.attributes || {};
    const fotoArtikel = StoffUndStahlPersonalisierung.#fotoArtikel(cart);
    this.vorhandenImWarenkorb = Boolean(fotoArtikel && a[ATTRIBUTE.modell]);
    // Die Adressen der schon hochgeladenen Fotos in die versteckten Felder schreiben,
    // damit eine Folgeposition dieselben Fotos selbst trägt. Rührt nichts Sichtbares an.
    const props = (fotoArtikel && fotoArtikel.properties) || {};
    for (const feld of this.fotoUrlFelder) {
      const wert = String(props[FOTO_PROPERTIES[feld.dataset.susFotourl]] || '');
      if (feld.value !== wert) feld.value = wert;
    }
    if (this.zeitstempel && !this.aendernAktiv) {
      const zeit = String(props['_personalisiert_am'] || '');
      if (zeit && this.zeitstempel.value !== zeit) this.zeitstempel.value = zeit;
    }
    return { a, fotoArtikel };
  }

  /**
   * Nur den Stand nachlesen. Rührt die Eingaben nicht an - sonst würde ein
   * Auffrischen mitten im Tippen die Eingaben des Kunden überschreiben.
   */
  async #warenkorbAuffrischen() {
    try {
      this.#standUebernehmen(await this.#korbHolen());
      this.#zustandAktualisieren();
    } catch (e) {
      // Ohne Warenkorbdaten bleibt der bisherige Stand stehen
    }
  }

  async #warenkorbLesen() {
    try {
      const cart = await this.#korbHolen();
      const { a, fotoArtikel } = this.#standUebernehmen(cart);
      if (fotoArtikel && a[ATTRIBUTE.modell] && !this.aendernAktiv) {
        const infotext = a[ATTRIBUTE.infotext] || '';
        this.vorhandenText.textContent =
          `Deine Personalisierung ist hinterlegt: ${MODELL_NAMEN[a[ATTRIBUTE.modell]] || a[ATTRIBUTE.modell]}, ` +
          `drei Fotos, Infotext mit ${infotext.length} Zeichen. Sie gilt für alle Artikel dieser Bestellung.`;
        this.vorhanden.hidden = false;
        this.formular.hidden = true;
        // Formular mit den vorhandenen Werten vorbelegen, falls der Kunde „ändern“ wählt
        this.modellSelect.value = a[ATTRIBUTE.modell];
        this.textarea.value = infotext;
        this.textUnveraendert = false;
        this.#zaehlerAktualisieren();
      }
    } catch (e) {
      // Ohne Warenkorbdaten einfach das Formular zeigen
    }
    this.#weiterSchalten();
    // Der Warenkorb wird erst nach dem Aufbau gelesen; die Sperre muss
    // danach neu bewertet werden (Folgeartikel brauchen keine Angaben).
    this.#zustandAktualisieren();
  }

  /**
   * true: Foto-Felder und Zeitstempel gehen mit dem Formular mit (neue Personalisierung).
   * false: nur die Markierung „siehe erste Position“ geht mit (Folgeartikel).
   */
  #fotoFelderSchalten(neu) {
    const setzen = (feld, wert) => { if (feld && feld.disabled !== wert) feld.disabled = wert; };
    // Datei- und Adressfelder tragen denselben Property-Namen: immer genau eine Gruppe.
    for (const input of this.dateiInputs) setzen(input, !neu);
    for (const feld of this.fotoUrlFelder) setzen(feld, neu || !feld.value);
    // Der Zeitstempel geht immer mit - bei einer neuen Personalisierung der frische,
    // bei einer Folgeposition der aus dem Warenkorb übernommene.
    setzen(this.zeitstempel, false);
    // Der geprüfte Wert gehört zu den hochgeladenen Dateien und geht nur mit ihnen mit.
    setzen(this.minKanteFeld, !neu);
  }

  // ---------- Modell und Text ----------

  #modellGewechselt() {
    if (this.textUnveraendert || this.textarea.value.trim() === '') {
      this.#standardtextEinsetzen(false);
    }
  }

  #standardtextEinsetzen(erzwingen) {
    const text = this.texte[this.modellSelect.value] || '';
    if (!erzwingen && !this.textUnveraendert && this.textarea.value.trim() !== '') return;
    this.textarea.value = text;
    this.textUnveraendert = true;
    this.#zaehlerAktualisieren();
  }

  #zaehlerAktualisieren() {
    const n = this.textarea.value.length;
    this.zaehler.textContent = `${n} / ${this.maxChars} Zeichen`;
  }

  // ---------- Fotos ----------

  async #dateiPruefen(input) {
    const key = input.dataset.susDatei;
    const status = input.closest('[data-sus-foto]').querySelector('[data-sus-status]');
    delete this.geprueft[key];
    status.className = 'sus__status';
    status.textContent = '';

    const file = input.files && input.files[0];
    if (!file) return;

    if (file.size > this.maxBytes) {
      this.#panne('foto_zu_gross', `${file.size} ueber ${this.maxBytes}`,
        { ansicht: key, bytes: file.size, format: file.type });
      status.classList.add('sus__status--fehler');
      status.textContent = `Datei zu groß (${(file.size / 1024 / 1024).toFixed(1)} MB, erlaubt sind ${Math.round(this.maxBytes / 1024 / 1024)} MB).`;
      input.value = '';
      return;
    }

    status.textContent = 'Foto wird geprüft …';
    let masse;
    try {
      masse = await this.#bildmasse(file);
    } catch (e) {
      this.#panne('foto_lesen', e, { ansicht: key, bytes: file.size, format: file.type });
      status.classList.add('sus__status--fehler');
      status.textContent = 'Das Bild lässt sich nicht lesen. Bitte ein JPEG- oder PNG-Foto wählen (HEIC-Fotos vom iPhone vorher als JPEG exportieren oder direkt vom Handy hochladen).';
      this.#fehlerZeigen(`${input.dataset.susBeschriftung}: ${status.textContent}`);
      input.value = '';
      return;
    }

    // Regel: für alle drei Ansichten zählt die lange Kante, Hochkant und Querformat gleich
    const massgeblich = Math.max(masse.width, masse.height);
    if (massgeblich < this.minKante) {
      this.#panne('foto_zu_klein', `${massgeblich} unter ${this.minKante}`,
        { ansicht: key, bytes: file.size, format: file.type,
          breite: masse.width, hoehe: masse.height });
      status.classList.add('sus__status--fehler');
      status.textContent =
        `Lange Kante nur ${massgeblich} Pixel, nötig sind mindestens ${this.minKante}. ` +
        'Bitte das Originalfoto in voller Auflösung verwenden (nicht aus einem Chat weitergeleitet).';
      this.#fehlerZeigen(`${input.dataset.susBeschriftung}: ${status.textContent}`);
      input.value = '';
      return;
    }

    // In JPEG wandeln und auf MAX_KANTE begrenzen; das gewandelte Bild geht mit dem Formular hoch.
    //
    // Gewandelt wird JEDES Foto, nicht nur ein zu großes. Vorher stand hier
    // "zuGross || kein JPEG", und das drehte die Größen um: Ein Foto mit 4484 px ging
    // unangetastet mit 4,88 MB hoch, während ein größeres mit 5328 px auf 2,73 MB gerechnet
    // wurde — das kleinere Foto erzeugte den größeren Upload (gemessen 05.10.2026). Das
    // Original bleibt nur, wenn es nach dem Wandeln wirklich kleiner wäre.
    let endgueltig = file;
    let hinweis = '';
    try {
      const gewandelt = await this.#alsJpeg(file, key);
      const lohnt = gewandelt.size < file.size || Math.max(masse.width, masse.height) > MAX_KANTE;
      if (lohnt) {
        endgueltig = gewandelt;
        const dt = new DataTransfer();
        dt.items.add(endgueltig);
        input.files = dt.files;
      }
    } catch (e) {
      // Umwandlung nicht möglich – dann geht das Original hoch
      this.#panne('foto_verkleinern', e, { ansicht: key, bytes: file.size,
        format: file.type, breite: masse.width, hoehe: masse.height });
      endgueltig = file;
    }
    if (endgueltig !== file) {
      hinweis = ` Für den Upload auf ${(endgueltig.size / 1024 / 1024).toFixed(1)} MB verkleinert.`;
    }

    // Die Quelle wird gemerkt: Reißt die Summe aller drei Fotos das Budget, wird noch einmal
    // kleiner gerechnet — und zwar aus dem Original, nicht aus dem schon komprimierten JPEG.
    // Zweimal nacheinander zu komprimieren kostet sichtbar Qualität.
    this.geprueft[key] = { file: endgueltig, quelle: file, width: masse.width, height: masse.height };
    this.#fehlerZeigen('');
    status.classList.add('sus__status--ok');
    status.textContent = `Passt: ${masse.width} × ${masse.height} Pixel.${hinweis}`;

    await this.#budgetEinhalten();
  }

  /**
   * Deckel gegen die 20-MB-Wand: Die drei Fotos gehen in EINER Anfrage hoch, und oberhalb von
   * etwa 20 MB lehnt Shopifys nginx sie mit 413 ab — lautlos, der Artikel landet nicht im
   * Warenkorb. Liegt die Summe über dem Budget, werden alle drei stufenweise kleiner gerechnet,
   * immer aus dem Original. Läuft erst, wenn alle drei Fotos da sind; vorher ist die Summe
   * ohnehin nicht bekannt.
   */
  async #budgetEinhalten() {
    const keys = Object.keys(FOTO_PROPERTIES).filter((k) => this.geprueft[k] && this.geprueft[k].quelle);
    if (keys.length < Object.keys(FOTO_PROPERTIES).length) return;

    const summe = () => keys.reduce((s, k) => s + this.geprueft[k].file.size, 0);
    if (summe() <= UPLOAD_BUDGET) return;

    for (const stufe of NOTFALL_STUFEN) {
      for (const k of keys) {
        try {
          const kleiner = await this.#alsJpeg(this.geprueft[k].quelle, k, stufe.kante, stufe.qualitaet);
          if (kleiner.size >= this.geprueft[k].file.size) continue;
          this.geprueft[k].file = kleiner;
          const input = this.dateiInputs.find((i) => i.dataset.susDatei === k);
          if (input) {
            const dt = new DataTransfer();
            dt.items.add(kleiner);
            input.files = dt.files;
            const umgebung = input.closest('[data-sus-foto]');
            const status = umgebung ? umgebung.querySelector('[data-sus-status]') : null;
            if (status) {
              status.textContent =
                `Passt: ${this.geprueft[k].width} × ${this.geprueft[k].height} Pixel.` +
                ` Für den Upload auf ${(kleiner.size / 1024 / 1024).toFixed(1)} MB verkleinert.`;
            }
          }
        } catch (e) {
          console.warn('[stoffundstahl] Nachverkleinern fehlgeschlagen', k, e);
          this.#panne('budget_verkleinern', e, {
            ansicht: k, bytes: this.geprueft[k].quelle.size,
            format: this.geprueft[k].quelle.type,
            breite: this.geprueft[k].width, hoehe: this.geprueft[k].height,
          });
        }
      }
      if (summe() <= UPLOAD_BUDGET) return;
    }

    // Selbst die kleinste Stufe reicht nicht. Lieber ehrlich sagen als in den 413 laufen.
    this.#panne('budget_zu_gross', `Summe ${summe()} ueber ${UPLOAD_BUDGET}`, { bytes: summe() });
    this.#fehlerZeigen(
      `Deine drei Fotos ergeben zusammen ${(summe() / 1024 / 1024).toFixed(1)} MB und sind damit zu groß ` +
      'für einen Upload. Bitte nimm etwas kleinere Fotos, aus der Kamera-App statt aus einem Chat.'
    );
  }

  /** Bild dekodieren (mit EXIF-Drehung), auf die Kante begrenzen, als JPEG zurückgeben. */
  async #alsJpeg(file, key, kante = MAX_KANTE, qualitaet = JPEG_QUALITAET) {
    const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const faktor = Math.min(1, kante / Math.max(bmp.width, bmp.height));
    const w = Math.round(bmp.width * faktor);
    const h = Math.round(bmp.height * faktor);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bmp, 0, 0, w, h);
    bmp.close();
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob fehlgeschlagen'))), 'image/jpeg', qualitaet);
    });
    return new File([blob], `${key}.jpg`, { type: 'image/jpeg' });
  }

  /** Breite und Höhe nach EXIF-Drehung ermitteln. */
  async #bildmasse(file) {
    if ('createImageBitmap' in window) {
      try {
        const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
        const m = { width: bmp.width, height: bmp.height };
        bmp.close();
        return m;
      } catch (e) {
        // Fallback unten
      }
    }
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Bild nicht lesbar'));
      };
      img.src = url;
    });
  }

  // ---------- Angaben sichern ----------

  /**
   * Modell, Infotext und Bildrechte sind Warenkorb-Attribute und müssen vor
   * dem Hinzufügen gespeichert sein. Frueher geschah das beim Absenden: Das
   * Formular wurde abgefangen, gespeichert und dann erneut abgeschickt.
   *
   * Das ging nicht auf. Horizon haengt seine Handler mit `capture: true` an
   * `document` (assets/component.js) und wird im Kopf der Seite geladen -
   * also lief Horizons Handler immer zuerst und legte den Artikel sofort in
   * den Warenkorb. Das erneute Abschicken erzeugte dann eine zweite Anfrage,
   * rund 340 ms spaeter. Ein Klick, zwei Positionen.
   *
   * Darum wird jetzt nichts mehr abgefangen. Gespeichert wird, sobald die
   * Angaben vollstaendig sind; bis die Antwort da ist, bleibt der Kaufknopf
   * gesperrt. Beim Klick gibt es nichts mehr zu tun - Horizon sendet allein.
   */
  #fingerabdruck() {
    return JSON.stringify([
      this.modellSelect.value,
      this.textarea.value.trim(),
      this.rechte.checked ? 'ja' : '',
    ]);
  }

  /** Steht alles, was gespeichert werden muss? */
  #angabenVollstaendig() {
    if (!this.modellSelect.value) return false;
    if (!this.rechte.checked) return false;
    if (this.textarea.value.length > this.maxChars) return false;
    for (const input of this.dateiInputs) {
      if (!this.geprueft[input.dataset.susDatei]) return false;
    }
    return true;
  }

  /** Sichern anstossen, leicht verzoegert - sonst je Tastendruck eine Anfrage. */
  #sichernAnstossen() {
    clearTimeout(this.sichernTimer);
    this.sichernTimer = setTimeout(() => this.#angabenSichern(), 500);
  }

  async #angabenSichern() {
    const fp = this.#fingerabdruck();
    // gescheitertAn verhindert, dass ein dauerhaft fehlschlagender Aufruf
    // alle 500 ms wiederholt wird. Ändert der Kunde etwas, greift es wieder.
    if (this.sichertGerade || fp === this.gesichert || fp === this.gescheitertAn) return;
    this.sichertGerade = true;

    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          attributes: {
            [ATTRIBUTE.modell]: this.modellSelect.value,
            [ATTRIBUTE.infotext]: this.textarea.value.trim(),
            [ATTRIBUTE.rechte]: 'ja',
          },
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const cart = await res.json();
      if (!cart.attributes || cart.attributes[ATTRIBUTE.modell] !== this.modellSelect.value) {
        throw new Error('Attribute wurden nicht gespeichert');
      }

      // Die Fotos gehen als Line-Item-Properties mit dem Produktformular hoch;
      // das Schalten der Felder übernimmt #zustandAktualisieren() im finally.
      if (this.zeitstempel) this.zeitstempel.value = new Date().toISOString();
      this.gesichert = fp;
      this.gescheitertAn = null;
      this.#fehlerZeigen('');
    } catch (e) {
      console.error('[stoffundstahl] Speichern fehlgeschlagen', e);
      this.#panne('attribute_speichern', e, {});
      this.gesichert = null;
      this.gescheitertAn = fp;
      this.#fehlerZeigen(
        'Die Angaben konnten nicht gespeichert werden. Ändere eine Angabe oder lade die Seite neu. Wenn es wieder nicht klappt, schreib uns.'
      );
    } finally {
      this.sichertGerade = false;
      this.#zustandAktualisieren();
    }
  }


  /**
   * Ladezustand am Kaufknopf (Teil c, 05.10.2026).
   *
   * Beginnt mit dem Absenden und endet, wenn der Warenkorb eine Position mehr hat. Dazwischen
   * ist der Knopf gesperrt und beschriftet sich selbst mit laufender Sekundenzahl — vorher
   * passierte nach dem Druck sichtbar gar nichts, und genau das haben Kunden als „der Shop ist
   * kaputt" gelesen.
   *
   * Abgefangen wird nichts: Horizon sendet selbst ab, hier wird nur zugesehen.
   */
  #ladenStarten() {
    if (this.laedt) return;
    this.laedt = true;
    this.laedtSeit = Date.now();
    // Welche Variante gerade abgeschickt wird. Das ist das verlaessliche
    // Erfolgsmerkmal: Laege sie schon im Warenkorb, waere der Kaufknopf gesperrt
    // gewesen (#schonImWarenkorb). Sie kann also nur durch dieses Absenden
    // hineinkommen - unabhaengig davon, welche Antwort zuerst zurueckkommt.
    const idFeld = this.form ? this.form.querySelector('[name="id"]') : null;
    this.laufVariante = idFeld && idFeld.value ? String(idFeld.value) : null;
    this.#fehlerZeigen('');
    this.#zustandAktualisieren();
    // Jede Sekunde neu beschriften; #knoepfeSetzen schreibt nur bei echter Änderung.
    this.laedtTakt = setInterval(() => this.#knoepfeSetzen(), 1000);
    this.#aufWarenkorbWarten();
  }

  #ladenBeenden(erfolg) {
    if (!this.laedt) return;
    this.laedt = false;
    // Dem Server sagen, dass wirklich etwas im Warenkorb gelandet ist. Ohne
    // dieses Signal sieht niemand, dass Kunden Warenkoerbe fuellen - und die
    // Fruehwarnung "Warenkoerbe, aber niemand an der Kasse" haette keine
    // Grundlage (20.10.2026). Es geht KEINE Mailadresse und kein Foto mit.
    if (erfolg) this.#melden('warenkorb_gefuellt');
    clearInterval(this.laedtTakt);
    this.laedtTakt = null;
    if (!erfolg) this.#fehlerZeigen(this.textLaedtFehler);
    this.#warenkorbLesen();
  }

  /**
   * Erfolg heißt: der Warenkorb hat eine Position mehr als vor dem Absenden.
   *
   * Am Zeitstempel ließe es sich nicht festmachen — ein Folgeartikel trägt denselben wie die
   * Position, die schon im Warenkorb liegt, der wäre also bereits vor dem Absenden „gefunden".
   */
  async #aufWarenkorbWarten() {
    let vorher = null;
    let zuletzt = null;
    try {
      vorher = (await this.#korbHolen()).item_count;
    } catch (e) {
      vorher = null;
    }
    while (this.laedt && Date.now() - this.laedtSeit < UPLOAD_GEDULD_MS) {
      await new Promise((r) => setTimeout(r, 1500));
      if (!this.laedt) return;
      try {
        const korb = await this.#korbHolen();

        // Erstes und wichtigstes Merkmal: Liegt die abgeschickte Variante drin?
        // Die Zaehlung darunter hat bis zum 20.10.2026 allein entschieden, und sie
        // ist ein Wettlauf: Horizon schickt /cart/add.js VOR unserem /cart.js los
        // (beide bei t=0, belegt im Pruefstand). Kam die Antwort auf das Hinzufuegen
        // zuerst, stand in `vorher` schon der neue Stand - dann konnte er nie
        // wachsen, der Knopf blieb 90 Sekunden gesperrt und zeigte danach eine
        // Fehlermeldung, obwohl der Artikel im Warenkorb lag.
        if (this.laufVariante
            && (korb.items || []).some((i) => String(i.variant_id) === this.laufVariante
                                           || String(i.id) === this.laufVariante)) {
          return this.#ladenBeenden(true);
        }

        const jetzt = korb.item_count;
        zuletzt = jetzt;
        if (vorher === null) {
          vorher = jetzt;
          continue;
        }
        if (jetzt > vorher) return this.#ladenBeenden(true);
      } catch (e) {
        // Netz kurz weg - weiter versuchen, bis die Geduld abgelaufen ist
      }
    }
    if (this.laedt) {
      // Nur hinsehen (20.10.2026): Nach 90 Sekunden gibt der Knopf auf und zeigt
      // eine Fehlermeldung. Die beiden Zaehler gehen mit, weil sie den Unterschied
      // machen: Steht am Ende dieselbe Zahl, ist der Artikel wirklich nicht
      // angekommen. Ist sie gewachsen, war der Fehlschlag nur gemessen - dann liegt
      // es an der Zaehlung, nicht am Upload.
      this.#panne('warenkorb_zeitlimit',
        `vorher ${vorher}, zuletzt ${zuletzt}`,
        { bytes: Object.keys(this.geprueft).reduce(
          (sa, k) => sa + (this.geprueft[k].file ? this.geprueft[k].file.size : 0), 0) });
      this.#ladenBeenden(false);
    }
  }

  /**
   * Die Beschriftung des Kaufknopfs steht als nackter Textknoten zwischen Horizons Einkaufswagen
   * und einem leeren span. Deshalb wird dieser Knoten gesucht und sein ursprünglicher Text am
   * Knopf gemerkt, statt den Inhalt des Knopfes zu ersetzen.
   */
  static #knopfBeschriftung(knopf) {
    for (const k of knopf.childNodes) {
      if (k.nodeType === Node.TEXT_NODE && k.textContent.trim()) return k;
    }
    return null;
  }

  #dateiname(key, file) {
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
    return `${key}.${ext}`;
  }

  #fehlerZeigen(text) {
    this.fehler.textContent = text;
    this.fehler.hidden = !text;
    if (text) this.fehler.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  #hinweisZeigen(text) {
    this.hinweis.textContent = text;
    this.hinweis.hidden = !text;
  }
}

if (!customElements.get('stoffundstahl-personalisierung')) {
  customElements.define('stoffundstahl-personalisierung', StoffUndStahlPersonalisierung);
}
